/* =============================================================
   Dashboard.jsx — Admin home screen
   -------------------------------------------------------------
   Displays the main overview for the IO manager:
   - Four stat cards (total students, outgoing, incoming, partners)
   - "Upcoming Tasks" pipeline table with urgency scoring,
     type/level filters, and pagination (5 rows per page)
   - Upcoming deadlines sidebar

   Data is fetched from Supabase via the useDashboard hook.
   The pipeline is sorted by urgency score — flagged and
   incomplete cases appear first.

   ✓ DONE (backend integration):
   - Replaced mockData imports with useDashboard hook
   - Removed module-level computed constants (now from hook)
   - Removed PROFILE_ROUTE map (routes come from hook as /incoming/:id)
   - Removed local urgency helpers (logic lives in useDashboard)
   - Added skeleton loaders while data is loading
   - Kept hardcoded deadlines array (no DB table yet)
   ============================================================= */

import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Layout from '../components/Layout';
import { useDashboard } from '../hooks/useDashboard';
import { useAuth } from '../context/AuthContext';


/* ─── STATUS BADGE STYLE ─────────────────────────────────────── */
// Dynamic: step labels are configured by the IO manager, so we can't
// hardcode a color per label. Use emerald when exchange is complete
// (student on the last step) and indigo for any in-progress step.
function statusBadgeStyle(isCompleted) {
  return isCompleted
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : 'bg-indigo-100 text-indigo-700 border border-indigo-200';
}


/* ─── UPCOMING DEADLINES ─────────────────────────────────────── */
// Static reminder cards shown in the right-hand sidebar.
// TODO: replace with a Supabase fetch when a deadlines table exists.
const deadlines = [
  {
    month: 'OCT', day: '15', color: 'bg-red-100 text-red-700',
    title: 'Partner Nominations',
    desc:  'Final day to submit outgoing student list to Asia-Pacific partners.',
  },
  {
    month: 'OCT', day: '28', color: 'bg-indigo-100 text-indigo-700',
    title: 'Visa Info Session',
    desc:  'Mandatory workshop for all incoming non-EU students.',
  },
  {
    month: 'NOV', day: '05', color: 'bg-slate-100 text-slate-600',
    title: 'Transcript Submission',
    desc:  'Deadline for returning students to upload external transcripts.',
  },
];

const PER_PAGE = 5;


/* ─── AVATAR ─────────────────────────────────────────────────── */
// Generates a coloured circle with the student's initials.
function Avatar({ name }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const colors   = ['bg-indigo-500', 'bg-violet-500', 'bg-sky-500', 'bg-emerald-500', 'bg-rose-500'];
  const idx      = name.charCodeAt(0) % colors.length;
  return (
    <div className={`w-7 h-7 ${colors[idx]} rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
}


/* ─── STAT CARD SKELETON ─────────────────────────────────────── */
function StatCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-pulse">
      <div className="w-10 h-10 rounded-xl bg-slate-100 mb-4" />
      <div className="h-8 w-16 bg-slate-100 rounded mb-2" />
      <div className="h-4 w-28 bg-slate-100 rounded mb-1" />
      <div className="h-3 w-20 bg-slate-50 rounded" />
    </div>
  );
}


/* ─── PIPELINE ROW SKELETON ──────────────────────────────────── */
function PipelineRowSkeleton() {
  return (
    <tr className="border-b border-slate-50 animate-pulse">
      <td className="px-6 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-slate-100 flex-shrink-0" />
          <div className="space-y-1.5">
            <div className="h-4 w-32 bg-slate-100 rounded" />
            <div className="h-3 w-48 bg-slate-50 rounded" />
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5"><div className="h-4 w-24 bg-slate-100 rounded" /></td>
      <td className="px-4 py-3.5"><div className="h-5 w-16 bg-slate-100 rounded-full" /></td>
      <td className="px-4 py-3.5"><div className="h-5 w-20 bg-slate-100 rounded-full" /></td>
      <td className="px-4 py-3.5"><div className="h-4 w-4 bg-slate-100 rounded" /></td>
    </tr>
  );
}


/* ─── DASHBOARD COMPONENT ────────────────────────────────────── */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName  = user?.name?.split(' ')[0] ?? 'back';
  const university = user?.university ?? 'your university';

  const { stats, pipeline: allPipeline, loading, error, refetch } = useDashboard();
  const showSkeleton = loading || (!stats && !error);

  const [page,          setPage]          = useState(1);
  const [typeFilter,    setTypeFilter]    = useState('All');
  const [urgencyFilter, setUrgencyFilter] = useState('All');

  // Reset page to 1 whenever a filter changes
  const setFilter = (setter) => (val) => { setter(val); setPage(1); };

  const filteredPipeline = allPipeline
    .filter(r => typeFilter    === 'All' || r.type  === typeFilter)
    .filter(r => urgencyFilter === 'All' || r.level === urgencyFilter);

  const totalPages = Math.ceil(filteredPipeline.length / PER_PAGE);
  const pipeline   = filteredPipeline.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <Layout>
      {/* Page title */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Welcome back, {firstName} 👋</h1>
        <p className="text-[#464555] mt-1">Your exchange overview for {university} this semester.</p>
      </div>

      {/* Error banner — shown when auto-retries exhausted and we have no data */}
      {error && !stats && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-red-500 mt-0.5">error</span>
            <div>
              <p className="text-sm font-semibold text-red-800">Couldn't load the dashboard</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[16px]">refresh</span>
            Retry
          </button>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {showSkeleton ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-slate-600 text-[20px]">group</span>
              </div>
              <p className="font-display text-3xl font-bold text-[#0b1c30]">{stats.totalStudents}</p>
              <p className="text-[#464555] text-sm mt-1">Total Students</p>
              <p className="text-xs text-slate-400 mt-0.5">This semester</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-indigo-600 text-[20px]">flight_takeoff</span>
              </div>
              <p className="font-display text-3xl font-bold text-[#0b1c30]">{stats.outgoingCount}</p>
              <p className="text-[#464555] text-sm mt-1">Outgoing Students</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {stats.outgoingActive} active · {stats.outgoingCompleted} completed
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-sky-600 text-[20px]">flight_land</span>
              </div>
              <p className="font-display text-3xl font-bold text-[#0b1c30]">{stats.incomingCount}</p>
              <p className="text-[#464555] text-sm mt-1">Incoming Students</p>
              <p className="text-xs text-slate-400 mt-0.5">{stats.incomingActive} active · {stats.incomingCompleted} completed</p>
            </div>

            <div className="bg-[#4343d5] rounded-2xl p-6 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-white text-[20px]">account_balance</span>
              </div>
              <p className="font-display text-3xl font-bold text-white">{stats.partnerCount}</p>
              <p className="text-indigo-200 text-sm mt-1">Active Partners</p>
              <p className="text-xs text-indigo-300 mt-0.5">{stats.countryCount} countries</p>
            </div>
          </>
        )}
      </div>

      {/* Upcoming Tasks pipeline table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {/* Table header with filters */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">Upcoming Tasks</h2>
            {!loading && stats?.urgentCount > 0 && (
              <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                <span className="material-symbols-outlined text-[13px]">warning</span>
                {stats.urgentCount} urgent
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={typeFilter}
              onChange={e => setFilter(setTypeFilter)(e.target.value)}
              className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
            >
              <option value="All">All Types</option>
              <option value="Incoming">Incoming</option>
              <option value="Outgoing">Outgoing</option>
            </select>

            <select
              value={urgencyFilter}
              onChange={e => setFilter(setUrgencyFilter)(e.target.value)}
              className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
            >
              <option value="All">All Levels</option>
              <option value="Urgent">Urgent</option>
              <option value="Action Needed">Action Needed</option>
              <option value="Monitoring">Monitoring</option>
            </select>

            {/* Urgency levels tooltip */}
            <div className="relative group">
              <span className="material-symbols-outlined text-[16px] text-slate-300 cursor-pointer hover:text-slate-500 transition-colors">info</span>
              <div className="absolute right-0 top-6 z-50 hidden group-hover:block w-64 bg-[#0b1c30] text-white text-xs rounded-xl shadow-xl p-3 space-y-2.5">
                <p className="font-semibold text-slate-300 uppercase tracking-wide text-[10px] mb-1">Urgency Levels</p>
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 mt-0.5"></span>
                  <div><span className="font-semibold">Urgent:</span> Immediate action required — deadline at risk or partner follow-up overdue.</div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0 mt-0.5"></span>
                  <div><span className="font-semibold">Action Needed:</span> Something is missing or pending — documents, LA, or status update required.</div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0 mt-0.5"></span>
                  <div><span className="font-semibold">Monitoring:</span> On track — no immediate action needed, just keep an eye on progress.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table rows */}
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-50 bg-slate-50/40">
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-6 py-3">Students · Actions needed</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3">Destination / Origin</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3">Type</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {showSkeleton ? (
              Array.from({ length: 5 }).map((_, i) => <PipelineRowSkeleton key={i} />)
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-sm text-red-500">
                  <span className="material-symbols-outlined text-[18px] mr-1 align-middle">error</span>
                  {error}
                </td>
              </tr>
            ) : pipeline.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-sm text-slate-400">
                  No students match the selected filters
                </td>
              </tr>
            ) : (
              pipeline.map((row, i) => (
                <tr
                  key={row.id ?? i}
                  onClick={() => navigate(row.route)}
                  className={`border-b border-slate-50 last:border-b-0 hover:bg-[#F0F2FF] transition-colors cursor-pointer group ${
                    row.level === 'Urgent'        ? 'border-l-2 border-l-red-400'   :
                    row.level === 'Action Needed' ? 'border-l-2 border-l-amber-400' :
                    'border-l-2 border-l-slate-200'
                  }`}
                >
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={row.student} />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-[#0b1c30]">{row.student}</p>
                          {row.isUrgent && (
                            <span className="material-symbols-outlined text-red-500 text-[14px]">priority_high</span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 ${
                          row.level === 'Urgent'        ? 'text-red-500'   :
                          row.level === 'Action Needed' ? 'text-amber-500' :
                          'text-slate-400'
                        }`}>{row.action}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-[#464555] max-w-[140px] truncate">{row.destination}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      row.type === 'Incoming' ? 'bg-sky-100 text-sky-700' : 'bg-indigo-100 text-indigo-700'
                    }`}>{row.type}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusBadgeStyle(row.isCompleted)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="material-symbols-outlined text-slate-300 text-[18px] group-hover:text-indigo-400 transition-colors">
                      arrow_forward
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination — only render when not loading and there is data */}
        {!loading && filteredPipeline.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filteredPipeline.length)} of {filteredPipeline.length} students
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[16px]">chevron_left</span>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    n === page ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[16px]">chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
